/**
 * Idle guard for a stream (a search, an agent turn): fires `onStall` when the stream goes quiet for `timeoutMs`.
 *
 * `reset()` means "the stream is alive" and must be called for EVERY frame received — including a
 * frame that carries no loads. Resetting only on frames that carried loads turns this into a "time
 * since the last load" timer, which fires on a healthy search whose sources are still reporting in
 * over a sparse lane.
 *
 * The guard is cosmetic: `onStall` clears the progress UI. It does not abort the stream, and loads
 * that arrive afterwards still render.
 */
interface StreamIdleGuard {
    /** Restart the countdown. Call on stream open and on every frame. */
    reset: () => void
    /** Stop the countdown — the stream ended, so a later stall is meaningless. */
    cancel: () => void
}

export function createStreamIdleGuard(timeoutMs: number, onStall: () => void): StreamIdleGuard {
    let timer: ReturnType<typeof setTimeout> | null = null
    const cancel = () => {
        if (timer) clearTimeout(timer)
        timer = null
    }
    return {
        reset: () => {
            cancel()
            timer = setTimeout(() => {
                timer = null
                onStall()
            }, timeoutMs)
        },
        cancel,
    }
}
